import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import {
  buildRecurringRideSummary,
  isRecurringRideSummaryReady,
} from '../../../utils/buildRecurringRideSummary';
import { DATE_TIME_FIELD_LABELS } from '../../forms/CreateRide/StepThree/stepThreeDateTimeConstants';
import { toSerializableRecurrenceRule } from '../../../utils/helpers';
import { previewRecurringSeries } from '../../../store/rides';

import styles from './index.module.css';

const EMPTY_SERIES = [];

const toPreviewRuleKey = (rule) => {
  if (!rule) return '';
  return [
    rule.frequency,
    rule.count,
    rule.interval,
    rule.isCustom,
    rule.monthlyType,
    rule.weekIndex,
    Array.isArray(rule.byDays) ? rule.byDays.join(',') : '',
    rule.startPoint?.datetime,
    rule.startPoint?.timezone,
    rule.returnStartPoint?.datetime,
    rule.returnStartPoint?.timezone,
    rule.appointment?.datetime,
    rule.appointment?.timezone,
  ].join('|');
};

/**
 * Recurring series summary card (Step 3, Step 5 review, Quick Schedule).
 * @param {'form'|'review'} variant form = Step 3 spacing; review = Step 5 spacing
 * @param {boolean} dense smaller label/value fonts (Quick Schedule only)
 * @param {boolean} showSeriesList View Rides In Series accordion (Create Ride Step 3 / Step 5 only)
 */
export const RecurringRidePlanSummary = ({
  isRecurring,
  isRoundtrip,
  recurrenceRule,
  pickUpDate,
  variant = 'form',
  dense = false,
  showSeriesList = true,
  className,
}) => {
  const dispatch = useDispatch();
  const seriesRides = useSelector(
    (state) => state.rides?.recurringSeriesRides ?? EMPTY_SERIES,
  );
  const seriesLoading = useSelector((state) =>
    Boolean(state.rides?.recurringSeriesLoading),
  );
  const [seriesOpen, setSeriesOpen] = useState(true);

  const show = useMemo(
    () => Boolean(isRecurring) && isRecurringRideSummaryReady(recurrenceRule),
    [isRecurring, recurrenceRule],
  );

  const summary = useMemo(() => {
    if (!show) return null;
    return buildRecurringRideSummary(recurrenceRule, pickUpDate, Boolean(isRoundtrip));
  }, [show, recurrenceRule, pickUpDate, isRoundtrip]);

  const previewRule = useMemo(() => {
    if (!show || !recurrenceRule) return null;
    const startPoint = recurrenceRule.startPoint?.datetime
      ? recurrenceRule.startPoint
      : pickUpDate;
    if (!startPoint?.datetime || !startPoint?.timezone) return null;

    return toSerializableRecurrenceRule({
      ...recurrenceRule,
      startPoint,
      interval: recurrenceRule.interval || 1,
    });
  }, [show, recurrenceRule, pickUpDate]);

  const previewRuleKey = useMemo(() => toPreviewRuleKey(previewRule), [previewRule]);

  useEffect(() => {
    setSeriesOpen(true);
  }, [previewRuleKey]);

  useEffect(() => {
    if (!seriesOpen || !previewRule || !previewRuleKey) return undefined;

    dispatch(previewRecurringSeries(previewRule));
  }, [dispatch, seriesOpen, previewRuleKey]); // eslint-disable-line react-hooks/exhaustive-deps -- previewRule keyed by previewRuleKey

  if (!show) return null;

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.rootReview]: variant === 'review',
        [styles.rootForm]: variant === 'form',
        [styles.rootDense]: dense,
      })}
    >
      <h3 className={styles.heading}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_TITLE}</h3>
      <div className={styles.planCard}>
        <div className={styles.planTopRow}>
          <div className={styles.planCell}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_FIRST_RIDE}</span>
            <span className={styles.planValue}>{summary?.startDate || '—'}</span>
          </div>
          <div className={styles.planCell}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_LAST_RIDE}</span>
            <span className={styles.planValue}>{summary?.endDate || '—'}</span>
          </div>
          <div className={classNames(styles.planCell, styles.planCellTotal)}>
            <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_TOTAL_RIDES}</span>
            <span className={styles.planValue}>{summary?.numberOfRides || '—'}</span>
          </div>
        </div>
        <div className={styles.planBottomRow}>
          <span className={styles.planLabel}>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_REPEATS}</span>
          <span className={styles.planValue}>{summary?.repeats || '—'}</span>
        </div>
      </div>

      {showSeriesList && previewRule && (
        <div className={styles.seriesSection}>
          <button
            type="button"
            className={styles.seriesToggle}
            aria-expanded={seriesOpen}
            onClick={() => setSeriesOpen((open) => !open)}
          >
            <span>{DATE_TIME_FIELD_LABELS.RIDE_SUMMARY_VIEW_SERIES}</span>
            <svg
              className={seriesOpen ? styles.seriesChevronOpen : styles.seriesChevronClosed}
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              aria-hidden
            >
              <path
                d="M0.999976 4.8L4.79998 1L8.59998 4.8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {seriesOpen && (
            <div className={styles.seriesListWrap}>
              <div className={styles.seriesList}>
                {seriesLoading ? (
                  <div className={styles.seriesLoader}>
                    <Spin size="default" />
                  </div>
                ) : (
                  <>
                    {seriesRides.map((ride, index) => (
                      <div key={`${ride.date}-${ride.time}-${index}`} className={styles.seriesRow}>
                        <span className={styles.seriesDate}>{ride.date}</span>
                        {ride.holidayName ? (
                          <span className={styles.seriesHoliday}>{ride.holidayName}</span>
                        ) : (
                          <span className={styles.seriesHolidaySpacer} />
                        )}
                        <span className={styles.seriesTime}>{ride.time}</span>
                      </div>
                    ))}
                    {seriesRides.length === 0 && (
                      <div className={styles.seriesEmpty}>No rides in series</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
